import React, { useEffect, memo, forwardRef, useCallback, useState } from 'react';
import Sidebar from './Sidebar';
import MobileNavigation from './MobileNavigation';
import Footer from './Footer';
import { useAppStore } from '@/store';
import { Toaster } from "@/components/ui/toaster";
import { useNavigationType, useNavigate, useLocation, Outlet } from 'react-router-dom'; // Import Outlet
import { Download, LogIn, LogOut, User, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { MessageSquare, Cpu, Brain, Beaker, GraduationCap, Microscope, Code, Library, Users, Settings } from 'lucide-react';
import UserNotifications from '@/components/users/UserNotifications';
import { getNavItems } from '@/lib/navigation';
import BugReportModal from '@/components/feedback/BugReportModal';
import { useMessagingStore } from '@/store/useMessagingStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import UserMessaging from '@/components/users/UserMessaging';
import OnboardingChecklistModal from '@/components/onboarding/OnboardingChecklistModal';
import UserActions from '@/components/layout/UserActions';

const MainNavigation = memo(() => {
  const isMobile = useIsMobile();
  const location = useLocation(); // Get current location

  // Determine if we are on the research page
  const isResearchPage = location.pathname === '/research';

  if (isMobile) {
    return (
      <div className="flex space-x-2">
        <Link to="/" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
          Home
        </Link>
        <Link to="/news" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
          News
        </Link>
        <Link to="/purchase" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
          Products
        </Link>

      </div>
    );
  }
  
  return (
    <div className="flex space-x-2">
      {/* Conditionally render "Home" link if not on research page */}
      {!isResearchPage && (
        <Link to="/" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
          Home
        </Link>
      )}
      <Link to="/about" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
        About
      </Link>
      <Link to="/news" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
        News
      </Link>
      <Link to="/purchase" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
        Products
      </Link>

      <Link to="/forums" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
        Forums
      </Link>
      <Link to="/docs" className="px-3 py-2 text-sm rounded-md hover:bg-accent">
        Docs
      </Link>
    </div>
  );
});
MainNavigation.displayName = 'MainNavigation';

export const DownloadButton = forwardRef<HTMLAnchorElement, React.AnchorHTMLAttributes<HTMLAnchorElement>>((props, ref) => {
  return (
    <Button 
      variant="ghost" 
      size="icon" 
      className="rounded-full hover:bg-primary/10 text-primary transition-all"
      asChild
    >
      <Link to="/downloads" {...props} ref={ref}>
        <Download className="h-5 w-5" />
      </Link>
    </Button>
  );
});
DownloadButton.displayName = 'DownloadButton';

const MainLayout = memo(({ children }: { children?: React.ReactNode }) => { // children is now optional
  const { darkMode } = useAppStore();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
    return isEmbedded ? true : false;
  });
  const [showBugReport, setShowBugReport] = useState(false);
  const { activeMessagingUser, closeMessaging } = useMessagingStore();
  
  const isAuthPage = location.pathname === '/auth';
  // Pages that manage their own header/layout
  const isMarketingPage = ['/research', '/partnership', '/maplept', '/privacy', '/desktop'].includes(location.pathname);
  const isOSPage = location.pathname.startsWith('/desktop/start') || location.pathname.startsWith('/start');

  // Treat the unauthenticated home route ('/') as a marketing page for proper window scrolling
  const isLandingPage = location.pathname === '/' && !user;

  let isRouterAvailable = true;
  try {
    useNavigationType();
  } catch (e) {
    isRouterAvailable = false;
    console.log('Router context not available, sidebar will not be shown');
  }

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Allow window/root scrolling on marketing/landing pages to avoid nested scrollbars
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    if (isMarketingPage || isLandingPage) {
      root.classList.add('allow-window-scroll');
    } else {
      root.classList.remove('allow-window-scroll');
    }
    return () => {
      root.classList.remove('allow-window-scroll');
    };
  }, [isMarketingPage, isLandingPage]);

  const navItems = React.useMemo(() => {
    return getNavItems(location.pathname, setShowBugReport).filter(item => 
      !['/about', '/news', '/purchase', '/forums', '/docs'].includes(item.path)
    );
  }, [location.pathname]);

  // Additional header nav items for mobile hamburger menu
  const mobileMenuItems = React.useMemo(() => {
    const allNavItems = getNavItems(location.pathname, setShowBugReport, true); // Pass true for isMobile
    return allNavItems.filter(item => 
      ['/news', '/forums', '/docs'].includes(item.path)
    );
  }, [location.pathname]);

  if (isAuthPage) {
    return (
      <div className="h-full w-full flex flex-col">
        <main className="flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto">
            {children}
          </div>
        </main>
      </div>
    );
  }

  // Handle Full-Screen OS Page (No scroll, fixed container)
  if (isOSPage) {
    return (
      <div className="fixed inset-0 h-full w-full overflow-hidden bg-background">
        {children || <Outlet />}
      </div>
    );
  }

  // Handle Marketing/Landing Pages (Native Window Scroll)
  if (isMarketingPage || isLandingPage) {
    return (
      <div
        className="relative w-full min-h-[100dvh] bg-background z-[100]"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children || <Outlet />}
        {showBugReport && <BugReportModal open={showBugReport} onOpenChange={setShowBugReport} />}
      </div>
    );
  }

  return (
    <div className="flex h-full w-full overflow-hidden bg-background h-[100dvh]"> {/* Main container */}
      {isRouterAvailable && !isMobile && (
        <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      )}
      
      <div className="flex flex-1 flex-col h-full overflow-hidden relative"> {/* Main content column */}
        {/* Header - Standard App Header */}
        <div className="flex-none h-14 bg-card flex justify-between items-center px-4 border-b z-10">
            <MainNavigation />
            <div className="flex items-center space-x-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {!isMobile && <DownloadButton />}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download Desktop App</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <UserNotifications />
              <UserActions />
            </div>
        </div>

        {/* Page Content - scrolls independently */}
        <main 
          className={`flex-1 overflow-y-auto overflow-x-hidden bg-background relative ${isMobile ? 'pb-20' : ''}`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children || <Outlet />} 
        </main>
        
        {/* Footer */}
        {!isMobile && (
          <div className="flex-none bg-card z-10">
            <Footer />
          </div>
        )}
        {/* Onboarding checklist modal (opens only if user hasn't completed) */}
        <OnboardingChecklistModal />
      </div>
      
      {/* Mobile Navigation */}
      {isRouterAvailable && isMobile && (
        <MobileNavigation 
          navItems={navItems}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          UserActionsComponent={UserActions}
          DownloadButtonComponent={DownloadButton}
        />
      )}
      {showBugReport && <BugReportModal open={showBugReport} onOpenChange={setShowBugReport} />}

      {activeMessagingUser && (
        <Dialog open={!!activeMessagingUser} onOpenChange={closeMessaging}>
          <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0">
            <DialogHeader className="py-3 px-6 border-b flex-shrink-0">
              <DialogTitle>Message {activeMessagingUser.name}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <UserMessaging
                userId={activeMessagingUser.id}
                userName={activeMessagingUser.name}
                onClose={closeMessaging}
                hideInternalHeader={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

MainLayout.displayName = 'MainLayout';

export default MainLayout;